// Non-blocking level-up card picker. CRITICAL: this modal does NOT
// swallow keyboard events for movement — WASD keeps feeding the
// controller while the cards are visible, so the player can still
// dodge NPC chase damage while choosing. See feedback memory
// `feedback_levelup_no_movement_block.md`.
//
// Stacks: if multiple levels come in back-to-back, we queue them
// internally and show the next roll as soon as the player picks.

import type { Powerup, DerivedStats } from '../match/powerups';
import { RARITY_COLOR, deriveStats, phaseForLevel } from '../match/powerups';
import { makePowerupIcon, POWERUP_ICON_SIZE } from '../art/powerup_icons';
import { COMBAT, FACTION_DMG_MULT, FACTION_SPEED_MULT, type Role } from '../net/protocol';

type PendingRoll = {
  level: number;
  cards: Powerup[];
};

export type LevelUpContext = {
  role: Role;
  powerups: ReadonlyArray<string>;
  level: number;
};

export class LevelUpModal {
  private root: HTMLDivElement;
  private queue: PendingRoll[] = [];
  private current: PendingRoll | null = null;
  private onPick: (p: Powerup) => void = () => {};
  private hotkeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private getCtx: () => LevelUpContext = () => ({ role: 'int', powerups: [], level: 1 });

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'ui';
    // pointer-events: none so the underlying canvas still gets input;
    // only the card buttons themselves receive clicks. This is what
    // lets WASD keep feeding the controller.
    this.root.style.cssText = `
      left: 50%; bottom: 12%;
      transform: translateX(-50%);
      display: none;
      pointer-events: none;
      z-index: 40;
    `;
    parent.appendChild(this.root);
  }

  setOnPick(handler: (p: Powerup) => void) {
    this.onPick = handler;
  }

  setContext(provider: () => LevelUpContext) {
    this.getCtx = provider;
  }

  enqueue(level: number, cards: Powerup[]) {
    if (cards.length === 0) return;
    this.queue.push({ level, cards });
    if (!this.current) this.showNext();
  }

  isOpen(): boolean {
    return this.current !== null;
  }

  hide() {
    this.root.style.display = 'none';
    this.root.innerHTML = '';
    if (this.hotkeyHandler) {
      removeEventListener('keydown', this.hotkeyHandler);
      this.hotkeyHandler = null;
    }
    this.current = null;
  }

  reset() {
    this.queue = [];
    this.hide();
  }

  private showNext() {
    const next = this.queue.shift();
    if (!next) { this.hide(); return; }
    this.current = next;
    this.render(next);
    this.bindHotkeys();
  }

  private pick(p: Powerup) {
    this.onPick(p);
    // small delay so the click animation reads
    this.hide();
    if (this.queue.length > 0) {
      setTimeout(() => this.showNext(), 80);
    }
  }

  private bindHotkeys() {
    this.hotkeyHandler = (e: KeyboardEvent) => {
      if (!this.current) return;
      // UI now shows at most 2 choices. Hotkeys stay bound dynamically
      // to the actual card count so a 1-card render only reacts to [1].
      const digit = e.code.startsWith('Digit') ? Number(e.code.slice(5)) : NaN;
      if (!Number.isFinite(digit)) return;
      const idx = digit - 1;
      if (idx < 0 || idx >= this.current.cards.length) return;
      const card = this.current.cards[idx];
      if (!card) return;
      e.preventDefault();
      this.pick(card);
    };
    addEventListener('keydown', this.hotkeyHandler);
  }

  private render({ level, cards }: PendingRoll) {
    const ctx = this.getCtx();
    const cur = deriveStats(ctx.powerups, undefined, ctx.level);
    this.root.style.display = 'block';
    const header = `
      <div style="
        text-align:center;
        margin-bottom:8px;
        font: 800 14px system-ui;
        letter-spacing:2px;
        color:#fde047;
        text-shadow: 0 2px 8px rgba(0,0,0,.8);
        pointer-events: none;
      ">LEVEL ${level} — PICK ONE</div>
    `;
    const stripHtml = renderStatStrip(cur, ctx.role);
    const cardMarkup = cards
      .map((c, i) => cardHtml(c, i, cur, ctx.powerups, ctx.role, ctx.level))
      .join('');
    this.root.innerHTML = `
      ${header}
      ${stripHtml}
      <div style="
        display:flex;
        gap:12px;
        justify-content:center;
      ">${cardMarkup}</div>
    `;
    const buttons = this.root.querySelectorAll<HTMLButtonElement>('button[data-idx]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const idx = Number(btn.dataset.idx);
        const p = cards[idx];
        if (p) this.pick(p);
      });
    });
  }
}

type DisplayStats = {
  hp: number;
  dmg: number;
  spd: number;
  crit: number;
  dodge: number;
};

function displayStats(s: DerivedStats, role: Role): DisplayStats {
  return {
    hp: COMBAT.HP_MAX + s.hpMaxAdd,
    dmg: s.damageMult * FACTION_DMG_MULT[role],
    spd: s.speedMult * FACTION_SPEED_MULT[role],
    crit: s.critRate,
    dodge: s.dodgeRate,
  };
}

function statCell(label: string, value: string, hot: boolean): string {
  const labelColor = hot ? '#fbbf24' : 'rgba(255,255,255,.45)';
  const valueColor = hot ? '#fbbf24' : '#e5e7eb';
  return `
    <span style="display:inline-flex;flex-direction:column;align-items:center;padding:0 10px;line-height:1.1;">
      <span style="font:800 8px system-ui;letter-spacing:1.5px;color:${labelColor};">${label}</span>
      <span style="font:900 13px system-ui;color:${valueColor};margin-top:2px;">${value}</span>
    </span>
  `;
}

function renderStatStrip(cur: DerivedStats, role: Role): string {
  const d = displayStats(cur, role);
  const sep = `<div style="width:1px;height:20px;background:rgba(255,255,255,.12);"></div>`;
  return `
    <div style="
      display:flex;justify-content:center;align-items:center;
      background:rgba(8,10,14,.92);
      border:1px solid rgba(255,255,255,.12);
      border-radius:10px;
      padding:6px 4px;
      margin:0 auto 10px;
      width:max-content;
      box-shadow:0 4px 14px rgba(0,0,0,.5);
      pointer-events:none;
    ">
      <span style="
        font:800 9px system-ui;letter-spacing:2px;color:rgba(255,255,255,.55);
        padding:0 12px 0 8px;border-right:1px solid rgba(255,255,255,.12);
      ">YOUR BUILD</span>
      ${statCell('HP', String(d.hp), d.hp > COMBAT.HP_MAX)}
      ${sep}
      ${statCell('DMG', `×${d.dmg.toFixed(2)}`, d.dmg > FACTION_DMG_MULT[role] + 0.001)}
      ${sep}
      ${statCell('SPD', `×${d.spd.toFixed(2)}`, d.spd > FACTION_SPEED_MULT[role] + 0.001)}
      ${sep}
      ${statCell('CRIT', `${Math.round(d.crit * 100)}%`, d.crit > 0)}
      ${sep}
      ${statCell('DODGE', `${Math.round(d.dodge * 100)}%`, d.dodge > 0)}
    </div>
  `;
}

function deltaRow(label: string, before: string, after: string): string {
  return `
    <div style="
      display:flex;justify-content:space-between;align-items:center;
      font:700 10px system-ui;padding:2px 4px;
      border-radius:4px;background:rgba(255,255,255,.04);margin-top:3px;
    ">
      <span style="color:rgba(255,255,255,.55);letter-spacing:1px;">${label}</span>
      <span style="white-space:nowrap;">
        <span style="color:rgba(255,255,255,.4);">${before}</span>
        <span style="opacity:.55;margin:0 4px;">→</span>
        <span style="color:#86efac;font-weight:900;">${after}</span>
      </span>
    </div>
  `;
}

function cardDeltaRows(
  before: DerivedStats,
  after: DerivedStats,
  role: Role,
): string {
  const dB = displayStats(before, role);
  const dA = displayStats(after, role);
  const rows: string[] = [];
  if (dA.hp !== dB.hp) {
    rows.push(deltaRow('HP', String(dB.hp), String(dA.hp)));
  }
  if (Math.abs(dA.dmg - dB.dmg) > 0.001) {
    rows.push(deltaRow('DMG', `×${dB.dmg.toFixed(2)}`, `×${dA.dmg.toFixed(2)}`));
  }
  if (Math.abs(dA.spd - dB.spd) > 0.001) {
    rows.push(deltaRow('SPD', `×${dB.spd.toFixed(2)}`, `×${dA.spd.toFixed(2)}`));
  }
  if (Math.abs(dA.crit - dB.crit) > 0.001) {
    rows.push(deltaRow(
      'CRIT',
      `${Math.round(dB.crit * 100)}%`,
      `${Math.round(dA.crit * 100)}%`,
    ));
  }
  if (Math.abs(dA.dodge - dB.dodge) > 0.001) {
    rows.push(deltaRow(
      'DODGE',
      `${Math.round(dB.dodge * 100)}%`,
      `${Math.round(dA.dodge * 100)}%`,
    ));
  }
  // Body shape + economy stats not on the strip — surface only when they change.
  if (Math.abs(after.circleRadiusMult - before.circleRadiusMult) > 0.001) {
    rows.push(deltaRow(
      'CIRCLE',
      `×${before.circleRadiusMult.toFixed(2)}`,
      `×${after.circleRadiusMult.toFixed(2)}`,
    ));
  }
  if (Math.abs(after.ringOuterMult - before.ringOuterMult) > 0.001) {
    rows.push(deltaRow(
      'RING',
      `×${before.ringOuterMult.toFixed(2)}`,
      `×${after.ringOuterMult.toFixed(2)}`,
    ));
  }
  if (Math.abs(after.hitboxShrink - before.hitboxShrink) > 0.001) {
    rows.push(deltaRow(
      'HITBOX',
      `-${Math.round(before.hitboxShrink * 100)}%`,
      `-${Math.round(after.hitboxShrink * 100)}%`,
    ));
  }
  if (Math.abs(after.absorptionMult - before.absorptionMult) > 0.001) {
    rows.push(deltaRow(
      'ABSORB',
      `×${before.absorptionMult.toFixed(2)}`,
      `×${after.absorptionMult.toFixed(2)}`,
    ));
  }
  if (Math.abs(after.xpMult - before.xpMult) > 0.001) {
    rows.push(deltaRow(
      'XP',
      `×${before.xpMult.toFixed(2)}`,
      `×${after.xpMult.toFixed(2)}`,
    ));
  }
  if (Math.abs(after.critMult - before.critMult) > 0.001) {
    rows.push(deltaRow(
      'CRIT×',
      `×${before.critMult.toFixed(1)}`,
      `×${after.critMult.toFixed(1)}`,
    ));
  }
  if (rows.length === 0) return '';
  return `<div style="margin-top:8px;">${rows.join('')}</div>`;
}

function cardHtml(
  p: Powerup,
  idx: number,
  curStats: DerivedStats,
  curStack: ReadonlyArray<string>,
  role: Role,
  level: number,
): string {
  const color = RARITY_COLOR[p.rarity];
  const rarityLabel = p.rarity.toUpperCase();
  const hotkey = (idx + 1).toString();
  const after = deriveStats(curStack, p.effect, level);
  const deltas = cardDeltaRows(curStats, after, role);
  const phase = phaseForLevel(level);
  const phaseLabel = phase === 3 ? 'III' : phase === 2 ? 'II' : 'I';
  const iconUrl = makePowerupIcon(p.id, phase);
  return `
    <button data-idx="${idx}" style="
      pointer-events: auto;
      width: 200px;
      min-height: 220px;
      background: rgba(10,12,18,.92);
      border: 2px solid ${color};
      border-radius: 14px;
      box-shadow: 0 8px 24px rgba(0,0,0,.55), 0 0 24px ${color}33;
      padding: 14px 12px 14px;
      font: 600 12px system-ui;
      color: #e5e7eb;
      text-align: center;
      cursor: pointer;
      transform: translateY(0);
      transition: transform .12s ease, box-shadow .12s ease;
    " onmouseover="this.style.transform='translateY(-4px)';this.style.boxShadow='0 14px 32px rgba(0,0,0,.65),0 0 36px ${color}66';"
       onmouseout="this.style.transform='translateY(0)';this.style.boxShadow='0 8px 24px rgba(0,0,0,.55),0 0 24px ${color}33';">
      <div style="
        display:flex;justify-content:center;align-items:center;gap:6px;
        font: 800 10px system-ui;
        letter-spacing: 2px;
        color: ${color};
        margin-bottom: 6px;
        text-shadow: 0 0 8px ${color}88;
      ">
        <span>${rarityLabel}</span>
        <span style="opacity:.55;">·</span>
        <span>PHASE ${phaseLabel}</span>
      </div>
      <img src="${iconUrl}" width="${POWERUP_ICON_SIZE}" height="${POWERUP_ICON_SIZE}"
        style="display:block;margin:0 auto 6px;image-rendering:pixelated;"
        alt="" aria-hidden="true" />
      <div style="
        font: 900 17px system-ui;
        color: #fafafa;
        margin-bottom: 6px;
        line-height: 1.1;
      ">${escapeHtml(p.name)}</div>
      <div style="
        font: 500 11px system-ui;
        color: #cbd5e1;
        opacity: .85;
        margin-bottom: 4px;
        min-height: 28px;
      ">${escapeHtml(p.blurb)}</div>
      ${deltas}
      <div style="
        display: inline-block;
        margin-top: 10px;
        padding: 2px 8px;
        border-radius: 10px;
        background: ${color}22;
        border: 1px solid ${color}66;
        font: 700 10px system-ui;
        color: ${color};
      ">[${hotkey}]</div>
    </button>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    c === '&' ? '&amp;' :
    c === '<' ? '&lt;' :
    c === '>' ? '&gt;' :
    c === '"' ? '&quot;' : '&#39;'
  ));
}
